// Vuex module — OITM Master Versioning.
//
// Draft CRUD + bulk import + template download + publish preview.
// Release list + cut + rollback + reconcile + export + calendar.
//
// Design ref: docs/project/oitm-master-versioning.md
import $axios from "../../api";

const PREFIX = "oitm-master";

const EMPTY_OITM_LIVE = { rows: [], total: 0, page: 1, per_page: 25, last_page: 1, from: 0, to: 0 };

const state = () => ({
  loading: false,
  drafts: [],
  releases: [],
  preview: { added: [], updated: [], noop: [], counts: { added: 0, updated: 0, noop: 0 } },
  calendar: { as_of: null, month: null, by_date: {} },
  currentRelease: null,
  // Penjelajah OITM live — paginasi SERVER-side (tabel oitm 3.853 baris), jadi state
  // menyimpan satu halaman + metadata, bukan koleksi utuh seperti `drafts`.
  oitmLive: { ...EMPTY_OITM_LIVE },
  oitmLoading: false,
});

const mutations = {
  SET_LOADING(s, v) { s.loading = v; },
  SET_DRAFTS(s, v) { s.drafts = v || []; },
  SET_OITM_LIVE(s, v) { s.oitmLive = v ? { ...EMPTY_OITM_LIVE, ...v } : { ...EMPTY_OITM_LIVE }; },
  SET_OITM_LOADING(s, v) { s.oitmLoading = v; },
  SET_RELEASES(s, v) { s.releases = v || []; },
  SET_PREVIEW(s, v) { s.preview = v || { added: [], updated: [], noop: [], counts: { added: 0, updated: 0, noop: 0 } }; },
  SET_CALENDAR(s, v) { s.calendar = v || { as_of: null, month: null, by_date: {} }; },
  SET_CURRENT_RELEASE(s, v) { s.currentRelease = v || null; },
};

const actions = {
  async fetchDrafts({ commit }) {
    commit("SET_LOADING", true);
    try {
      const res = await $axios.get(`${PREFIX}/drafts`);
      commit("SET_DRAFTS", res.data?.data?.rows || []);
    } catch (e) {
      // biarkan state default
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // Penjelajah OITM live. SENGAJA tidak menelan error seperti fetchDrafts/fetchReleases
  // di berkas ini — pola "// biarkan state default" itulah yang membuat 403 dari
  // middleware `access` tampil sebagai "tidak ada data", tak bisa dibedakan dari
  // sukses-dengan-nol-baris. Di sini error dilempar supaya tab bisa bilang apa
  // yang sebenarnya terjadi.
  async fetchOitmLive({ commit }, params = {}) {
    commit("SET_OITM_LOADING", true);
    try {
      const res = await $axios.get(`${PREFIX}/oitm`, { params });
      commit("SET_OITM_LIVE", res.data?.data);
      return res.data?.data;
    } finally {
      commit("SET_OITM_LOADING", false);
    }
  },

  async createDraft(_ctx, payload) {
    const res = await $axios.post(`${PREFIX}/drafts`, payload);
    return res.data?.data;
  },

  async updateDraft(_ctx, { id, ...payload }) {
    const res = await $axios.put(`${PREFIX}/drafts/${id}`, payload);
    return res.data?.data;
  },

  async deleteDraft(_ctx, id) {
    const res = await $axios.delete(`${PREFIX}/drafts/${id}`);
    return res.data?.data;
  },

  async importDrafts(_ctx, { format, file, contents, effective_from, reason }) {
    const fd = new FormData();
    fd.append("format", format);
    if (file) fd.append("file", file);
    if (contents !== undefined && contents !== null) fd.append("contents", contents);
    // Per-import, bukan per-baris — template tak punya kolom tanggal/alasan.
    if (effective_from) fd.append("effective_from", effective_from);
    if (reason) fd.append("reason", reason);
    const res = await $axios.post(`${PREFIX}/drafts/import`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data?.data;
  },

  // Download template — trigger browser download via anchor href (backend
  // stream response). Return URL agar caller bisa buat <a> click atau window.open.
  templateUrl(_ctx, format = "xlsx") {
    return `/api/${PREFIX}/template?format=${encodeURIComponent(format)}`;
  },

  async fetchPublishPreview({ commit }) {
    commit("SET_LOADING", true);
    try {
      const res = await $axios.get(`${PREFIX}/publish-preview`);
      commit("SET_PREVIEW", res.data?.data);
    } catch (e) {
      // biarkan state default
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchReleases({ commit }) {
    commit("SET_LOADING", true);
    try {
      const res = await $axios.get(`${PREFIX}/releases`);
      commit("SET_RELEASES", res.data?.data?.rows || []);
    } catch (e) {
      // biarkan state default
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchRelease({ commit }, id) {
    const res = await $axios.get(`${PREFIX}/releases/${id}`);
    commit("SET_CURRENT_RELEASE", res.data?.data);
    return res.data?.data;
  },

  async cutRelease(_ctx, payload) {
    const res = await $axios.post(`${PREFIX}/releases/cut`, payload || {});
    return res.data?.data;
  },

  async rollbackRelease(_ctx, { id, rollback_note }) {
    const res = await $axios.post(`${PREFIX}/releases/${id}/rollback`, { rollback_note });
    return res.data?.data;
  },

  async reconcileReleases() {
    const res = await $axios.post(`${PREFIX}/releases/reconcile`);
    return res.data?.data;
  },

  // Export → download zip. Response berupa blob; nama file dari Content-Disposition.
  async exportRelease(_ctx, id) {
    const res = await $axios.get(`${PREFIX}/releases/${id}/export`, { responseType: "blob" });
    const cd = res.headers?.["content-disposition"] || "";
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    return { blob: res.data, filename: m ? decodeURIComponent(m[1]) : `oitm-master-${id}.zip` };
  },

  async fetchCalendar({ commit }, asOf = null) {
    commit("SET_LOADING", true);
    try {
      const params = asOf ? { as_of: asOf } : {};
      const res = await $axios.get(`${PREFIX}/calendar`, { params });
      commit("SET_CALENDAR", res.data?.data);
    } catch (e) {
      // biarkan state default
    } finally {
      commit("SET_LOADING", false);
    }
  },
};

const getters = {
  hasDrafts: (s) => (s.drafts || []).length > 0,
  hasAddedInPreview: (s) => (s.preview.counts?.added || 0) > 0,
  publishableCount: (s) => (s.preview.counts?.updated || 0),
};

export default { namespaced: true, state, mutations, actions, getters };
